import {
  Alert,
  Box,
  Button,
  Divider,
  Grid2,
  Typography,
  useTheme,
} from "@mui/material";
import { useStyles } from "./styles";
import clsx from "clsx";
import PropTypes from "prop-types";
import TransferList from "components/transferList";
import { useNavigate } from "react-router-dom";
import SimpleDropdown from "components/dropdown/simpleDropdown";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import constants from "constants/constants.json";
import Notification from "components/snackbar";
import { useAuth0 } from "@auth0/auth0-react";
import BasicAccordion from "components/accordion";
import BasicTable from "components/table";
import SearchField from "components/simpleSearch";
import InfoIcon from "@mui/icons-material/Info";
import { UserContext } from "context/userContext";
import {
  getRolesAndProducts,
  getSSTProducts,
  getUserAssignedPermissions,
  editUser,
} from "./utils.js/userSettings.service";
import { getToken } from "utils/loadAuth";
import LoadingSpinner from "components/loadingSpinner";
import TransferListLoading from "components/transferList/transferListLoading";
import ModalPopup from "components/modalPopup";

const UserPermissionSettings = ({ handleClose, user, isNavbarOpen }) => {
  const theme = useTheme();
  const classes = useStyles({ theme, isNavbarOpen });
  const navigate = useNavigate();
  const [userType, setUserType] = useState(constants.access[user?.user_type]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);

  const [openNotification, setOpenNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [tableSearch, setTableSearch] = useState("");
  const contextValue = useContext(UserContext);
  const userContextValue = useMemo(() => contextValue || {}, [contextValue]);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [isUserTypeUpdated, setIsUserTypeUpdated] = useState(false);
  const [isSuperUserProductsLoading, setIsSuperUserProductsLoading] =
    useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isFetchingRef = useRef(false);

  const loggedInUserType = userContextValue?.loggedInUser?.user_type;

  const accessOptions = Object.entries(constants.access)
    .map(([id, Name]) => ({
      id: Number(id),
      Name,
    }))
    .filter((option) => {
      return option.id >= loggedInUserType;
    });

  const loggedInUserAssignedProducts =
    userContextValue?.loggedInUser?.product_names;

  const userDetails = useAuth0();
  const userPermissions = useMemo(
    () => userDetails?.user?.[constants.links.userPermissionsEndPoint] || [],
    [userDetails]
  );
  const userRoles = useMemo(
    () => userDetails?.user?.[constants.links.userRolesEndPoint] || [],
    [userDetails]
  );
  const userAssignedRoles = useMemo(
    () => new Set(userRoles.map((role) => role)),
    [userRoles]
  );

  const isProductDisabled = useCallback(
    (productName) => {
      if (loggedInUserType === 1 || loggedInUserType === 2) {
        return false;
      }
      const loggedInUserProducts = loggedInUserAssignedProducts
        ? loggedInUserAssignedProducts.split(",")
        : [];
      return !loggedInUserProducts.includes(productName);
    },
    [loggedInUserType, loggedInUserAssignedProducts]
  );

  const handleResultErrors = useCallback((errors) => {
    if (errors && errors.length > 0) {
      for (const error of errors) {
        console.error(
          `Error fetching data (index ${error.index}):`,
          error.reason
        );
      }
    }
  }, []);

  const processProducts = useCallback(
    (assignedProductsData) => {
      const sstProducts = assignedProductsData?.sstProducts;
      const products = assignedProductsData?.products;

      if (!sstProducts || !products) return;

      const formattedAssignedProducts = sstProducts.map((item) => ({
        productId: item.product_id,
        value: item.product_name,
        disabled: isProductDisabled(item.product_name),
      }));
      setAssignedProducts(formattedAssignedProducts);

      const productsList = products.map((item) => ({
        productId: item.product_id,
        value: item.product_name,
        disabled: isProductDisabled(item.product_name),
      }));

      const assignedProductNames = new Set(
        sstProducts.map((assigned) => assigned.product_name)
      );
      const availableProductsList = productsList.filter(
        (product) => !assignedProductNames.has(product.value)
      );

      setAvailableProducts(availableProductsList);
    },
    [isProductDisabled]
  );

  const processAvailableRoles = useCallback(
    (availableRolesData, assignedRolesData) => {
      if (!availableRolesData) return;

      const formattedRoles = availableRolesData.map((item) => ({
        roleId: item.id,
        value: item.roleName,
        disabled: item.disabled,
      }));

      const assignedRoleIds = new Set(
        (assignedRolesData?.data?.data?.items || []).map((role) => role.id)
      );
      const filteredRoles = formattedRoles.filter(
        (role) => !assignedRoleIds.has(role.roleId)
      );
      setAvailableRoles(filteredRoles);
    },
    []
  );

  const processAssignedRoles = useCallback(
    (assignedRolesData) => {
      const roleItems = assignedRolesData?.data?.data?.items;
      if (!roleItems) return;

      const formattedRoles = roleItems.map((role) => ({
        roleId: role.id,
        value: role.roleName,
        disabled:
          loggedInUserType === 1 || loggedInUserType === 2
            ? false
            : !userAssignedRoles.has(role.roleName),
      }));
      setAssignedRoles(formattedRoles);
    },
    [loggedInUserType, userAssignedRoles]
  );

  const finalizeLoading = useCallback(() => {
    setIsLoading(false);
    isFetchingRef.current = false;
  }, []);

  const getUser = useCallback(
    async (isUserUpdatedPostEdit = false) => {
      if (isFetchingRef.current) return;

      try {
        isFetchingRef.current = true;
        setIsLoading(true);

        const token = await getToken({
          getAccessTokenSilently,
          loginWithRedirect,
        });

        if (!user) {
          finalizeLoading();
          return;
        }
        const result = await getRolesAndProducts({
          userId: user?.user_id,
          userIdSub: user?.user_id_sub,
          userType: user?.user_type,
          isUserUpdatedPostEdit,
          token,
        });

        if (!result) {
          finalizeLoading();
          return;
        }

        handleResultErrors(result.errors);
        processProducts(result?.assignedProducts?.data);
        processAvailableRoles(
          result?.availableRoles?.data,
          result?.assignedRoles
        );
        processAssignedRoles(result?.assignedRoles);

        finalizeLoading();
      } catch (error) {
        console.error("Error fetching user data:", error);
        finalizeLoading();
      }
    },
    [
      getAccessTokenSilently,
      loginWithRedirect,
      user,
      handleResultErrors,
      processProducts,
      processAvailableRoles,
      processAssignedRoles,
      finalizeLoading,
    ]
  );

  const getUserPermissions = useCallback(
    async (isExpanded) => {
      setIsTableLoading(true);
      if (isExpanded) {
        try {
          const token = await getToken({
            getAccessTokenSilently,
            loginWithRedirect,
          });
          const result = await getUserAssignedPermissions({
            userId: user?.user_id_sub,
            token,
          });
          setIsTableLoading(false);
          if (result?.data?.data?.items) {
            const FunctionToCodeMap = {
              supplydemand: "Supply & Demand",
              panalysis: "Parametric Analysis",
              helvetia: "Helvetia Compliancy Check",
              all: "All",
            };

            const tableRows = result?.data?.data?.items?.map(
              (permission, index) => ({
                id: index,
                product: permission?.scope.split(":")[0] || "-",
                function:
                  FunctionToCodeMap[permission?.scope.split(":")[1]] || "-",
                template_link: permission?.scope.split(":")[2] || "-",
              })
            );
            setRows(tableRows);
          }
        } catch (error) {
          console.error("Error fetching user permissions:", error);
          setIsLoading(false);
          setIsTableLoading(false);
          setOpenNotification(true);
          setSeverity("error");
          setMessage(constants.errorMessage);
        }
      } else {
        setIsLoading(false);
        setIsTableLoading(false);
        setRows([]);
      }
    },
    [getAccessTokenSilently, loginWithRedirect, user?.user_id_sub]
  );

  useEffect(() => {
    const tableFilteredRows = rows?.filter(
      (row) =>
        row.product.toLowerCase().includes(tableSearch.toLowerCase()) ||
        row.template_link.toLowerCase().includes(tableSearch.toLowerCase())
    );
    setFilteredRows(tableFilteredRows);
  }, [rows, tableSearch]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  const columns = [
    {
      field: "product",
      headerName: "Product",
      flex: 1,
    },
    {
      field: "function",
      headerName: "Function",
      flex: 1,
    },
    {
      field: "template_link",
      headerName: "Template Link",
      flex: 2,
    },
  ];

  const saveUserDetails = useCallback(async () => {
    let sstProducts;

    if (isUserTypeUpdated) {
      const toBeRemoved = assignedProducts.filter(
        (product) =>
          !selectedProducts
            .map((selected) => selected.value)
            .includes(product.value)
      );

      sstProducts = {
        toBeAdded: [],
        toBeRemoved: toBeRemoved,
      };
    } else {
      const toBeAdded = selectedProducts.filter(
        (product) =>
          !assignedProducts
            .map((assigned) => assigned.value)
            .includes(product.value)
      );
      const toBeRemoved = assignedProducts.filter(
        (product) =>
          !selectedProducts
            .map((selected) => selected.value)
            .includes(product.value)
      );

      sstProducts = {
        toBeAdded: toBeAdded,
        toBeRemoved: toBeRemoved,
      };
    }

    let roles;
    if (selectedRoles.length > 0) {
      const rolesToBeAdded = selectedRoles.filter(
        (role) =>
          !assignedRoles.map((assigned) => assigned.value).includes(role.value)
      );
      const rolesToBeRemoved = assignedRoles.filter(
        (role) =>
          !selectedRoles.map((selected) => selected.value).includes(role.value)
      );

      roles = { rolesToBeAdded, rolesToBeRemoved };
    } else {
      roles = { rolesToBeAdded: [], rolesToBeRemoved: [] };
    }

    const token = await getToken({
      getAccessTokenSilently,
      loginWithRedirect,
    });

    const payload = {
      userId: user.user_id,
      userType: userType,
      sstProducts,
      roles,
    };
    setIsLoading(true);

    const { status } = await editUser({
      userId: payload.userId,
      token,
      formData: payload,
    });

    if (status === 403) {
      navigate("/forbidden");
      return;
    }
    if (status === 401) {
      navigate("/unauthorized");
      return;
    }

    if (status !== 200) {
      setIsLoading(false);
      setMessage(constants.errorMessage);
      setSeverity("error");
      setOpenNotification(true);
      return;
    }
    const loggedInUser = userContextValue?.loggedInUser;
    if (loggedInUser.user_id === user.user_id) {
      const updatedUserType = Object.entries(constants.access).find(
        ([k, v]) => v === userType
      )?.[0];

      const currentProducts = loggedInUser.product_names
        ? loggedInUser.product_names.split(",")
        : [];
      const toBeRemovedNames = sstProducts.toBeRemoved.map((p) => p.value);
      const toBeAddedNames = sstProducts.toBeAdded.map((p) => p.value);

      const updatedProducts = currentProducts
        .filter((product) => !toBeRemovedNames.includes(product))
        .concat(toBeAddedNames);

      userContextValue?.setLoggedInUser({
        ...loggedInUser,
        user_type: Number(updatedUserType),
        product_names: updatedProducts.join(","),
      });
    }

    await getUser(isUserTypeUpdated);
    setIsLoading(false);

    setMessage(constants.userSavedMessage);
    setSeverity("success");
    setOpenNotification(true);
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    user,
    userType,
    selectedRoles,
    selectedProducts,
    assignedProducts,
    assignedRoles,
    getUser,
    navigate,
    isUserTypeUpdated,
    userContextValue,
  ]);

  const handleSSTProductsError = useCallback((error) => {
    console.error("Error fetching SST products:", error);
    setIsSuperUserProductsLoading(false);
    setMessage(constants.errorMessage);
    setSeverity("error");
    setOpenNotification(true);
  }, []);

  const formatProductItems = useCallback(
    (productItems) => {
      if (!productItems) return [];

      return productItems.map((item) => ({
        productId: item.product_id,
        value: item.product_name,
        disabled: isProductDisabled(item.product_name),
      }));
    },
    [isProductDisabled]
  );

  const processSSTProductsData = useCallback(
    (data) => {
      const sstProducts = data?.sstProducts;
      const products = data?.products;

      if (!sstProducts || !products) return;

      const formattedAssignedProducts = formatProductItems(sstProducts);
      setAssignedProducts(formattedAssignedProducts);

      const productsList = formatProductItems(products);
      const assignedProductNames = new Set(
        sstProducts.map((assigned) => assigned.product_name)
      );
      const availableProductsList = productsList.filter(
        (product) => !assignedProductNames.has(product.value)
      );

      setAvailableProducts(availableProductsList);
    },
    [formatProductItems]
  );

  const processSSTProductsForSuperUser = useCallback(async () => {
    setIsSuperUserProductsLoading(true);

    try {
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });

      const result = await getSSTProducts({
        userId: user?.user_id,
        token,
      });

      processSSTProductsData(result?.data);
      setIsSuperUserProductsLoading(false);
    } catch (error) {
      handleSSTProductsError(error);
    }
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    user?.user_id,
    processSSTProductsData,
    handleSSTProductsError,
  ]);

  const checkUserTypeUpdateFlag = useCallback(
    (type) => {
      if (user?.user_type === 3 && type !== constants.access[3]) {
        setIsUserTypeUpdated(true);
      }
    },
    [user?.user_type]
  );

  const changeUserType = useCallback(
    async (type) => {
      setUserType(type);

      if (type === constants.access[3]) {
        await processSSTProductsForSuperUser();
      }

      checkUserTypeUpdateFlag(type);
    },
    [processSSTProductsForSuperUser, checkUserTypeUpdateFlag]
  );

  return (
    <Box className={classes.viewContainer}>
      <Box className={classes.titleWrapper}>
        <Typography
          data-testid="title"
          className={classes.title}
          variant="h5"
          align="center"
        >
          {constants.updateUserDetailsTitle}
        </Typography>
        <Box className={classes.actionWrapper}>
          <Button
            className={classes.back}
            data-testid="back-btn"
            onClick={() => {
              handleClose();
              navigate(constants.links.userManagement);
            }}
          >
            {constants.back}
          </Button>
        </Box>
      </Box>

      <Grid2 container className={classes.pageWrapper}>
        {isLoading ? (
          <Box id="loading" className={classes.loadingContainer}>
            <div id="loadingText">
              <LoadingSpinner loadingMessage={"Loading..."} />
            </div>
          </Box>
        ) : (
          <>
            <Grid2 xs={12} className={classes.transferSection}>
              <Box className={classes.roleWrapper}>
                <Typography
                  data-testid="user-type-title"
                  className={clsx(classes.text, classes.titles)}
                  variant="subtitle1"
                >
                  {constants.userDetails}
                </Typography>
              </Box>
              <Box className={classes.userDetailsWrapper}>
                <Typography
                  data-testid="user-type-title"
                  className={clsx(classes.details, classes.titles)}
                  variant="body2"
                >
                  {`${constants.name}: ${user?.firstname || ""} ${
                    user?.lastname || ""
                  }`}
                </Typography>
                <Typography
                  data-testid="user-type-title"
                  className={clsx(classes.details, classes.titles)}
                  variant="body2"
                >
                  {`${constants.email}: ${user?.email || ""}`}
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "row", gap: "10px" }}
                >
                  <Typography
                    data-testid="user-type-title"
                    className={clsx(classes.details, classes.titles)}
                    variant="body2"
                  >
                    {`${constants.userType}: `}
                  </Typography>
                  <SimpleDropdown
                    setDropdownValue={(event) =>
                      changeUserType(event.target.value)
                    }
                    value={userType || constants.access[user?.user_type]}
                    lists={accessOptions}
                  />
                </Box>
              </Box>
            </Grid2>
            {Number(
              Object.entries(constants.access).find(
                ([k, v]) => v === userType
              )?.[0]
            ) === 3 ? (
              <>
                <Divider className={classes.divider} />{" "}
                <Grid2 xs={12} className={classes.transferSection}>
                  <Typography
                    data-testid="products-title"
                    className={clsx(classes.text, classes.titles)}
                    variant="subtitle1"
                  >
                    {constants.SuperUserProductAssignments}
                  </Typography>

                  <Alert
                    data-testid="products-title"
                    className={classes.info}
                    icon={
                      <InfoIcon
                        sx={{ color: "rgb(91, 93, 110)" }}
                        fontSize="inherit"
                      />
                    }
                    severity="info"
                  >
                    {constants.productAssignInfoText}
                  </Alert>
                  {isSuperUserProductsLoading ? (
                    <TransferListLoading isNavbarOpen={isNavbarOpen} />
                  ) : (
                    <TransferList
                      isNavbarOpen={isNavbarOpen}
                      choicesTitle={constants.availableProducts}
                      chosenTitle={constants.assignedProducts}
                      availableList={availableProducts}
                      assignedList={assignedProducts}
                      setSelectedItems={(items) => setSelectedProducts(items)}
                    />
                  )}
                </Grid2>
              </>
            ) : (
              <Grid2 xs={12} className={classes.transferSection}>
                <Alert
                  data-testid="products-title"
                  className={classes.info}
                  icon={
                    <InfoIcon
                      sx={{ color: "rgb(91, 93, 110)" }}
                      fontSize="inherit"
                    />
                  }
                  severity="info"
                >
                  {constants.powerUserInfoText}
                </Alert>
              </Grid2>
            )}
            <Divider className={classes.divider} />
            <Grid2 xs={12} className={classes.transferSection}>
              <Box className={classes.roleWrapper}>
                <Typography
                  data-testid="roles-title"
                  className={clsx(classes.text, classes.titles)}
                  variant="subtitle1"
                >
                  {constants.roles}
                </Typography>
                {loggedInUserType !== 3 && (
                  <Button
                    onClick={() =>
                      navigate("/usermanagement/roles/addrole", {
                        state: { userId: user?.user_id, activeTab: "users" },
                      })
                    }
                    className={classes.addRoleButton}
                    data-testid="add-role-button"
                  >
                    {constants.createRoleTitle}
                  </Button>
                )}
              </Box>
              <Alert
                data-testid="products-title"
                className={classes.info}
                icon={
                  <InfoIcon
                    sx={{ color: "rgb(91, 93, 110)" }}
                    fontSize="inherit"
                  />
                }
                severity="info"
              >
                {constants.roleAssignInfoText}
              </Alert>
              <TransferList
                isNavbarOpen={isNavbarOpen}
                choicesTitle={constants.availableRoles}
                chosenTitle={constants.assignedRoles}
                availableList={availableRoles}
                assignedList={assignedRoles}
                setSelectedItems={(items) => setSelectedRoles(items)}
                permissions={userPermissions}
              />
            </Grid2>

            <Divider className={classes.divider} />
            <BasicAccordion
              setExpand={(isExpanded) => getUserPermissions(isExpanded)}
              title={constants.assignedPermissions}
              subtext={constants.viewAssignedPermissions}
            >
              <SearchField
                searchValue={tableSearch}
                setSearchValue={setTableSearch}
                placeholderText={constants.userPermissionsSearchText}
              />
              <Box className={classes.userPermissionsWrapper}>
                <BasicTable
                  disableColumnMenu
                  isLoading={isTableLoading}
                  showBorder={false}
                  rows={filteredRows}
                  columns={columns}
                  sxProps={{
                    ".MuiDataGrid-row": {
                      borderTop: "1px solid #f3f3f3 !important",
                    },
                  }}
                />
              </Box>
            </BasicAccordion>
            <Box className={classes.saveBtnWrapper}>
              <Button
                className={classes.saveBtn}
                data-testid="save-btn"
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                {constants.save}
              </Button>
            </Box>
          </>
        )}
      </Grid2>
      <ModalPopup
        handleClose={() => setIsModalOpen(false)}
        message={
          isUserTypeUpdated
            ? constants.saveUserMessage
            : constants.saveConfirmMessage
        }
        open={isModalOpen}
        primaryBtnText={constants.save}
        primaryBtnAction={() => {
          setIsModalOpen(false);
          saveUserDetails();
        }}
        secondaryButtonText={constants.cancel}
        secondarybtnAction={() => setIsModalOpen(false)}
        title={constants.saveUserDetails}
      />
      <Notification
        message={message}
        open={openNotification}
        setOpen={setOpenNotification}
        severity={severity}
      />
    </Box>
  );
};

UserPermissionSettings.propTypes = {
  handleClose: PropTypes.func,
  user: PropTypes.any,
  isNavbarOpen: PropTypes.bool,
};
export default UserPermissionSettings;
